































































import {
  ref,
  reactive,
  defineComponent,
  computed,
  onMounted,
  watch
} from "@vue/composition-api";
import Layout from "@/components/Atoms/Layout/index.vue";
import PageHeader from "@/components/Molecules/PageHeader/index.vue";
import LoadingView from "@/components/Molecules/LoadingView/index.vue";
import OccButton from "@/components/Atoms/OccButton/index.vue";
import ConfirmModal from "@/components/Organisms/modals/ConfirmModal/index.vue";
import OccAlertFloating from "@/components/Molecules/OccAlert/Floating/index.vue";
import { formatDate } from "@/helpers/date-helper";
import { useRequest } from "@/helpers/hooks/useRequest";
import ChartFilter from "./Molecules/ChartFilter/index.vue";
import ScoreCard from "./Molecules/ScoreCard/index.vue";
import EntryPvChart from "./Molecules/EntryPvChart/index.vue";
import UniversityEntryChart from "./Molecules/UniversityEntryChart/index.vue";
import { QueryParams } from "./Molecules/ChartFilter/types";
import { EventReport, CategoryReport, UniversityData } from "./types";
import { getPerformanceEvent, generatePerformanceEventCsv } from "@/api";
import { State } from "@/store/pages/performances/types";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import _, { chunk } from "lodash";

export default defineComponent({
  components: {
    Layout,
    PageHeader,
    LoadingView,
    OccButton,
    ConfirmModal,
    OccAlertFloating,
    ChartFilter,
    ScoreCard,
    EntryPvChart,
    UniversityEntryChart
  },
  name: "PerformanceEvent",
  setup(_, { root }) {
    // フィルター
    dayjs.extend(isoWeek);
    const start = dayjs();
    const end = dayjs(start);
    const defaultFilter: QueryParams = {
      eventId: 0,
      showCategory: "0",
      startDate: start.subtract(1, "months").format("YYYY/MM/DD"),
      endDate: end.subtract(1, "days").format("YYYY/MM/DD"),
      period: "week"
    };

    const query = reactive(defaultFilter);
    const showCsvDownloadModal = ref(false);
    const showCsvDownloadModalTitle = ref("");
    const showCsvDownloadModalDesc = ref("");
    const alertVisible = ref<Boolean>(false);
    const alertTextList = ref<string[]>([]);

    const [{ data: eventDetail, loading: getLoading }, runGetData] = useRequest(
      () => {
        const savedInput = root.$store.state.pages.performances;
        return getPerformanceEvent({
          event_id: savedInput.eventId || defaultFilter.eventId,
          start_date: savedInput.eventStartDate || defaultFilter.startDate,
          end_date: savedInput.eventEndDate || defaultFilter.endDate
        });
      },
      { immediate: false }
    );

    onMounted(() => {
      const savedInput = root.$store.state.pages.performances;
      query.eventId = savedInput.eventId || defaultFilter.eventId;
      query.startDate = savedInput.eventStartDate || defaultFilter.startDate;
      query.endDate = savedInput.eventEndDate || defaultFilter.endDate;
      // バックエンド改善までの暫定対応: query.showCategoryはdefaultFilter.showCategoryに固定
      // query.showCategory =
      //   savedInput.eventShowCategory || defaultFilter.showCategory;
      query.showCategory = defaultFilter.showCategory;
      query.period = savedInput.eventPeriod || defaultFilter.period;
      saveStore();
      if (savedInput.eventId === 0) {
        runGetData();
      }
    });

    /**
     * フィルター
     */
    const changeEventId = (id: number) => {
      query.eventId = id;
    };

    const setStartDate = (startDate: string) => {
      query.startDate = startDate;
    };

    const setEndDate = (endDate: string) => {
      query.endDate = endDate;
    };

    const setBusinessType = (show: string) => {
      query.showCategory = show;
    };

    const setPeriod = (period: string) => {
      query.period = period;
    };

    const saveStore = async () => {
      const payload: State = {
        eventId: query.eventId,
        eventStartDate: query.startDate,
        eventEndDate: query.endDate,
        eventShowCategory: query.showCategory,
        eventPeriod: query.period
      };
      root.$store.dispatch("pages/performances/saveEventChartFilter", payload);
    };

    watch(
      [() => query.eventId, () => query.startDate, () => query.endDate],
      async () => {
        await saveStore();
        runGetData();
      }
    );

    // APIの再取得が不要で、sessionStorageだけ更新するパラメータ
    watch([() => query.showCategory, () => query.period], async () => {
      await saveStore();
    });

    /**
     * イベント一覧
     */
    const initEventId = computed(() => {
      if (!eventDetail.value) {
        return 0;
      }
      return eventDetail.value.event_id;
    });

    const events = computed(() => {
      if (!eventDetail.value) {
        return [];
      }
      return eventDetail.value.events;
    });

    const displayPeriod = computed(() => query.period);
    const displayCategory = computed(() => query.showCategory);

    /**
     * Entry数、PV数
     */
    const totalEntry = computed(() => {
      if (!eventDetail.value) {
        return 0;
      }
      return eventDetail.value.total_entry_count;
    });

    const totalPv = computed(() => {
      if (!eventDetail.value) {
        return 0;
      }
      return eventDetail.value.total_pv;
    });

    /**
     * Entry&PVのチャートデータ
     */
    const pvEntryData = computed(() => {
      if (!eventDetail.value) {
        return [];
      }
      const res: EventReport[] = [];
      const chunkedData = chunk(eventDetail.value.event_reports, 200);

      chunkedData.forEach(e => {
        const tmp =
          query.period === "week" ? getWeeklyTotals(e) : getMonthlyTotals(e);
        res.push(...tmp);
      });

      const data = res.map(e => {
        return {
          date:
            query.period === "week"
              ? `${e.date.substring(0, 4)}/${e.date.substring(
                  4,
                  6
                )}/${e.date.substring(6, 8)}`
              : `${e.date.substring(0, 4)}/${e.date.substring(4, 6)}`,
          totalPv: e.total_pv,
          totalEntry: e.total_entry_count
        };
      });
      return data;
    });

    const categoryEntryData = computed(() => {
      if (!eventDetail.value || query.showCategory === "0") {
        return [];
      }
      const res: CategoryReport[] = [];
      const chunkedData = chunk(eventDetail.value.category_reports, 200);

      chunkedData.forEach(e => {
        const tmp =
          query.period === "week"
            ? getWeeklyCategoryTotals(e)
            : getMonthlyCategoryTotals(e);
        res.push(...tmp);
      });

      const data = res.map(e => {
        return {
          date:
            query.period === "week"
              ? `${e.date.substring(0, 4)}/${e.date.substring(
                  4,
                  6
                )}/${e.date.substring(6, 8)}`
              : `${e.date.substring(0, 4)}/${e.date.substring(4, 6)}`,
          totalPv: e.total_pv,
          totalEntry: e.total_entry_count
        };
      });
      return data;
    });

    const categoryName = computed(() => {
      return !eventDetail.value ? "" : eventDetail.value.business_category_name;
    });

    /**
     * 大学別のエントリーチャートデータ
     */
    const universityData = computed(() => {
      if (!eventDetail.value) {
        return [];
      }
      const res: UniversityData[] = [];
      const chunkedData = chunk(eventDetail.value.event_reports, 200);

      chunkedData.forEach(e => {
        const tmp =
          query.period === "week" ? getWeeklyTotals(e) : getMonthlyTotals(e);
        res.push(...tmp);
      });
      const data = res.map(e => {
        return {
          date:
            query.period === "week"
              ? `${e.date.substring(0, 4)}/${e.date.substring(
                  4,
                  6
                )}/${e.date.substring(6, 8)}`
              : `${e.date.substring(0, 4)}/${e.date.substring(4, 6)}`,
          kdd: e.kdd ?? 0,
          wkuik: e.wkuik ?? 0,
          gmarch: e.gmarch ?? 0,
          kkdr: e.kkdr ?? 0,
          ntks: e.ntks ?? 0,
          skkl: e.skkl ?? 0,
          national: e.national ?? 0,
          other: e.other ?? 0
        };
      });
      return data;
    });

    /**
     * CSVダウンロード
     */
    const onClickCsvDownload = () => {
      if (pvEntryData.value?.length || universityData.value?.length) {
        showCsvDownloadModalTitle.value = "CSVの作成を開始しました";
        showCsvDownloadModalDesc.value =
          "ダウンロードの準備ができたら、メールにて通知いたします。しばらくお待ちください。";
        showCsvDownloadModal.value = true;
        runCsvDownload({
          event_id: initEventId.value,
          start_date: query.startDate,
          end_date: query.endDate
        });
      } else {
        showCsvDownloadModalTitle.value = "ダウンロードする情報がありません";
        showCsvDownloadModal.value = true;
      }
    };

    const runCsvDownload = async (params: {
      event_id: number;
      start_date: string;
      end_date: string;
    }) => {
      const response = await generatePerformanceEventCsv(params);
      if (response.status === 200) {
        showCsvDownloadModal.value = true;
      } else {
        alertTextList.value = [
          "ダウンロードに失敗しました。リロードして再度お試しください。"
        ];
        showAlert();
      }
    };

    const closeCsvDownloadConfirm = () => {
      showCsvDownloadModal.value = false;
    };

    const showAlert = () => {
      alertVisible.value = true;
    };

    const closeAlert = () => {
      alertVisible.value = false;
    };

    const dateFormat = "YYYY/MM/DD";
    // リリース以前はデータが存在しないのでカレンダーで選択させないようにする
    const chartFilterMinDate = dayjs("2018/05/01").format(dateFormat);

    // 当日のデータはないので前日まで選択できるようにする
    const chartFilterMaxDate = dayjs()
      .subtract(1, "days")
      .format(dateFormat);

    return {
      getLoading,
      formatDate,
      totalEntry,
      runGetData,
      totalPv,
      events,
      eventDetail,
      query,
      initEventId,
      displayPeriod,
      displayCategory,
      categoryName,
      onClickCsvDownload,
      changeEventId,
      setStartDate,
      setEndDate,
      setBusinessType,
      setPeriod,
      pvEntryData,
      categoryEntryData,
      universityData,
      showCsvDownloadModal,
      closeCsvDownloadConfirm,
      alertVisible,
      alertTextList,
      showAlert,
      closeAlert,
      showCsvDownloadModalTitle,
      showCsvDownloadModalDesc,
      runCsvDownload,
      chartFilterMinDate,
      chartFilterMaxDate
    };
  }
});

const getMonthlyTotals = (data: Array<Object>): EventReport[] => {
  const res = _(data)
    .groupBy((item: any) => dayjs(item.date, "YYYYMM").format("YYYY-MM"))
    .mapValues(
      (
        group: [{ date: string; total_pv: number; total_entry_count: number }]
      ) => ({
        date: dayjs(group[0].date, "YYYYMM").format("YYYYMM"),
        total_pv: _.sumBy(group, "total_pv"),
        total_entry_count: _.sumBy(group, "total_entry_count"),
        kdd: _.sumBy(group, "kdd_entry_count"),
        wkuik: _.sumBy(group, "wkuik_entry_count"),
        gmarch: _.sumBy(group, "gmarch_entry_count"),
        kkdr: _.sumBy(group, "kkdr_entry_count"),
        ntks: _.sumBy(group, "ntks_entry_count"),
        skkl: _.sumBy(group, "skkl_entry_count"),
        national: _.sumBy(group, "national_entry_count"),
        other: _.sumBy(group, "other_entry_count")
      })
    )
    .filter(Boolean)
    .value();

  return (res as unknown[]) as EventReport[];
};

const getWeeklyTotals = (data: Array<Object>): EventReport[] => {
  const res = _(data)
    .groupBy((item: any) =>
      dayjs(item.date, "YYYYMMDD")
        .startOf("isoWeek")
        .format("YYYYMMDD")
    )
    .mapValues(
      (
        group: [{ date: string; total_pv: number; total_entry_count: number }]
      ) => ({
        date: dayjs(group[0].date, "YYYYMMDD")
          .startOf("isoWeek")
          .format("YYYYMMDD"),
        total_pv: _.sumBy(group, "total_pv"),
        total_entry_count: _.sumBy(group, "total_entry_count"),
        kdd: _.sumBy(group, "kdd_entry_count"),
        wkuik: _.sumBy(group, "wkuik_entry_count"),
        gmarch: _.sumBy(group, "gmarch_entry_count"),
        kkdr: _.sumBy(group, "kkdr_entry_count"),
        ntks: _.sumBy(group, "ntks_entry_count"),
        skkl: _.sumBy(group, "skkl_entry_count"),
        national: _.sumBy(group, "national_entry_count"),
        other: _.sumBy(group, "other_entry_count")
      })
    )
    .filter(Boolean)
    .value();

  return (res as unknown[]) as EventReport[];
};

const getMonthlyCategoryTotals = (data: Array<Object>): CategoryReport[] => {
  const res = _(data)
    .groupBy((item: any) => dayjs(item.date, "YYYYMM").format("YYYY-MM"))
    .mapValues(
      (
        group: [{ date: string; total_pv: number; total_entry_count: number }]
      ) => ({
        date: dayjs(group[0].date, "YYYYMM").format("YYYYMM"),
        total_pv: _.sumBy(group, "average_event_show_pv"),
        total_entry_count: _.sumBy(group, "average_entry_count")
      })
    )
    .filter(Boolean)
    .value();

  return (res as unknown[]) as CategoryReport[];
};

const getWeeklyCategoryTotals = (data: Array<Object>): CategoryReport[] => {
  const res = _(data)
    .groupBy((item: any) =>
      dayjs(item.date, "YYYYMMDD")
        .startOf("isoWeek")
        .format("YYYYMMDD")
    )
    .mapValues(
      (
        group: [{ date: string; total_pv: number; total_entry_count: number }]
      ) => ({
        date: dayjs(group[0].date, "YYYYMMDD")
          .startOf("isoWeek")
          .format("YYYYMMDD"),
        total_pv: _.sumBy(group, "average_event_show_pv"),
        total_entry_count: _.sumBy(group, "average_entry_count")
      })
    )
    .filter(Boolean)
    .value();

  return (res as unknown[]) as CategoryReport[];
};
